import { identifyAnalytics, resetAnalytics } from '@editor/global/analytics';
import basicUserInfoQuery from '@editor/queries/services/user/basicUserInfoQuery';
import {
  resetGrowthbookAttributes,
  setGrowthbookAttributes,
} from '@editor/services/growthbook';
import { clearAllWorkspaceProjectIdsFromLocalStorage } from '@editor/utils/localStorage';
import analytics from '@repo/analytics';
import { isNativeApp } from '@repo/detection';
import { User, type UserCreditInfo } from '@repo/domain/user';
import { queryClient } from '@repo/transport/queries';
import {
  getProfileToken,
  setProfileToken,
  setUserToken,
} from '@repo/transport/token';
import { sendLogoutMessage } from '@repo/transport/webview';
import { StateCreator } from 'zustand';

import userApi, { getUserToken } from '../../api/user';
import {
  TEMPLATE_BOOKMARK,
  USER_LOGIN,
  USER_LOGOUT,
  USER_SIGNUP,
} from '../../global/analytics/events';
import monitoring from '../../services/monitoring';
import { setUserContextMonitoring } from '../../services/rum';
import { ResponseWithError, PoWChallenge } from '../../types';
import { UtmData } from '../../types/UtmData';
import { useDesignsStore } from '../designsStore';
import { useNotificationStore } from '../notificationStore';
import { useWorkspaceStore } from '../workspaceStore';

import { applyUserPlan } from './_plan';
import { State } from './types';
import { updateOnboarding, getUserInfo, getUserCreditInfo } from './utils';

let getUserLoading: { resolve: (user: User | null) => void }[] = [];

export const stateCreator: StateCreator<State> = (set, get): State => ({
  /**
   * user state
   * contains information about the current user
   * false: state unclear, null: no user
   */
  user: false,

  /**
   * store templates that are bookmarked by a user
   */
  bookmarks: [],

  /**
   * Array where functions can be registered using
   * registerBeforeLoginAction. They are triggered in order before login
   */
  // TODO: this naming is not semantically correct anymore, should consider refactoring. Ref: https://app.clickup.com/t/2chcv6r
  beforeLoginActions: [],

  /**
   * Array where functions can be registered using
   * registerAfterSignupAction. They are triggered in order after signup
   */
  afterSignupActions: [],

  isLoading: false,

  isSigningOut: false,

  attributes: {},

  _loadUserInfo: async (token?: string): Promise<User | null> => {
    set({ isLoading: true });

    const user = applyUserPlan(await getUserInfo(token));
    if (user) {
      user.creditInfo = await getUserCreditInfo(token);
    }

    getUserLoading.forEach(({ resolve }) => resolve(user));
    getUserLoading = [];
    set({ isLoading: false });

    return user;
  },

  getUser: async (): Promise<State['user']> => {
    if (!get().isLoading) {
      return get().user;
    }

    return new Promise((resolve) => {
      getUserLoading.push({ resolve });
    });
  },

  getProfileToken,

  setProfileToken,

  _setUser: async (user: State['user']): Promise<void> => {
    if (user) {
      void updateOnboarding(user);
    }

    const previousUser = get().user;
    set({ user });

    if (user && (!previousUser || user.id !== previousUser?.id)) {
      get().beforeLogin(user);
      const result = await userApi.getBookmarkReferences();
      if (!('error' in result)) {
        const { templates } = result;
        set({ bookmarks: templates });
      }

      try {
        await identifyAnalytics(user);
      } catch (error) {
        console.error('identify analytics failed', error);
      }

      try {
        await setGrowthbookAttributes();
      } catch (error) {
        console.error('init growthbook attributes failed', error);
      }
    }
  },

  /**
   * initialize the user state
   */
  initialize: async (): Promise<void> => {
    if (get().user === false) {
      const user = await get()._loadUserInfo();
      await get()._setUser(user);
    }
  },
  /**
   * update the user with a new accessToken
   */
  updateUser: async (accessToken: string, isMainUser = true): Promise<void> => {
    const prevToken = get().getProfileToken();
    setProfileToken(accessToken);
    if (isMainUser) {
      setUserToken(accessToken);
    }
    if (prevToken === accessToken) {
      return;
    }

    const user = await get()._loadUserInfo(accessToken);
    await get()._setUser(user);

    // unset last used project as it may not belong to the new user
    clearAllWorkspaceProjectIdsFromLocalStorage();

    // invalidate all queries as access permissions may have changed
    // we don't await it because it starts requesting all queries in-band
    void queryClient.invalidateQueries();
  },
  /**
   * update the information about the current user
   */
  updateUserInfo: async (data?: Partial<User>): Promise<User | null> => {
    let user: User | null;
    const currentUser = get().user;
    if (data && currentUser) {
      // if user information and additional data is provided, merge them to set the user object
      user = { ...currentUser, ...data };
    } else {
      // otherwise, fetch up-to-date user information from the API
      void queryClient.invalidateQueries({
        queryKey: basicUserInfoQuery.queryKey,
      });
      user = await get()._loadUserInfo();
    }

    set({ user });
    return user;
  },

  updateUserCreditInfo: async (
    token?: string
  ): Promise<UserCreditInfo | null> => {
    const user = get().user;

    if (user) {
      const userCreditInfo = await getUserCreditInfo(token);
      set({ user: { ...user, creditInfo: userCreditInfo } });
      return userCreditInfo;
    }
    return null;
  },

  /**
   * create the user with a new accessToken and update
   */
  createUser: async ({
    name,
    email,
    password,
    invite,
    referral,
    promoCode,
    impactClickId,
    adClickId,
    utm,
    teamInviteId,
    signupUrl,
    deviceScreenSizeType,
    powChallenge,
  }: {
    name: string;
    email: string;
    password: string;
    invite: string;
    referral: string;
    promoCode: string;
    impactClickId: string;
    adClickId?: string;
    utm?: UtmData;
    teamInviteId?: string;
    signupUrl: string;
    deviceScreenSizeType?: string;
    powChallenge?: PoWChallenge;
  }): Promise<{ user: User; accessToken: string } | ResponseWithError> => {
    try {
      const res = await userApi.createUser({
        name,
        email,
        password,
        invite,
        referral,
        promoCode,
        impactClickId,
        adClickId,
        utm,
        teamInviteId,
        signupUrl,
        deviceScreenSizeType,
        powChallenge,
      });
      if ('accessToken' in res) {
        await get().updateUser(res.accessToken);
        analytics.track(USER_SIGNUP, {
          label: 'email',
          authenticationMethod: 'email',
          userId: res.id,
          invite,
          referral,
          promoCode,
          impactClickId,
          adClickId,
          leadsUserData: {
            email,
          },
        });
        get().afterSignup();
        return { accessToken: res.accessToken, user: get().user as User };
      } else {
        return res;
      }
    } catch (error) {
      return { error: String(error) };
    }
  },
  /**
   * login the user with a new accessToken and update
   */
  loginUser: async ({
    email,
    password,
    teamInviteId,
    powChallenge,
    otpCode,
    otpExchangeToken,
  }: {
    email: string;
    password: string;
    teamInviteId?: string;
    powChallenge?: PoWChallenge;
    otpCode?: string;
    otpExchangeToken?: string;
  }): Promise<
    | { user: User; accessToken: string }
    | ResponseWithError
    | { exchangeToken: string }
  > => {
    const res = await userApi.createSession(
      email,
      password,
      teamInviteId,
      powChallenge,
      otpCode,
      otpExchangeToken
    );

    if ('exchangeToken' in res) {
      // User is a 2FA user
      return { exchangeToken: res.exchangeToken };
    }

    if ('accessToken' in res) {
      await get().updateUser(res.accessToken);
      analytics.track(USER_LOGIN, {
        label: 'email',
        authenticationMethod: 'email',
        userId: res.id,
        leadsUserData: {
          email: email,
        },
      });

      return { accessToken: res.accessToken, user: get().user as User };
    } else {
      return res;
    }
  },
  /**
   * login the user with a new accessToken and update
   */
  loginTeam: async ({
    slug,
  }: {
    slug: string;
  }): Promise<{ user: User; token: string } | ResponseWithError> => {
    try {
      const res = await userApi.createTeamSession(slug);
      if ('token' in res) {
        await get().updateUser(res.token, false);
        return { token: res.token, user: get().user as User };
      } else {
        return res;
      }
    } catch (error) {
      return { error: String(error) };
    }
  },

  swapMainUser: async (): Promise<void> => {
    const token = getUserToken();
    if (token) {
      await get().updateUser(token, true);
    } else {
      // usually we would not expect a user to get here
      // but if they do, its better to logout than to do nothing
      await get().removeUser();
    }
  },
  /**
   * exchange a 3rd party token for a HT token and update cookie
   */
  exchangeTemporaryToken: async (
    token: string
  ): Promise<{ user: User; accessToken: string } | ResponseWithError> => {
    const res = await userApi.exchangeTemporaryToken(token);
    if ('accessToken' in res) {
      await get().updateUser(res.accessToken);
      const user = get().user;
      if (!user) {
        return { error: 'User not found' };
      }
      if (res.isSignup) {
        analytics.track(USER_SIGNUP, {
          label: res.name,
          authenticationMethod: res.name,
          userId: res.id,
          referral: res.referral,
          promoCode: res.promoCode,
          leadsUserData: {
            email: user.email,
          },
        });
      } else {
        analytics.track(USER_LOGIN, {
          label: res.name,
          authenticationMethod: res.name,
          userId: res.id,
          leadsUserData: {
            email: user.email,
          },
        });
      }
      return { accessToken: res.accessToken, user };
    } else {
      return res;
    }
  },
  /**
   * clean all current user state
   */
  removeUser: async (): Promise<void> => {
    setProfileToken(null);
    setUserToken(null);
    useNotificationStore.getState().reset();
    useDesignsStore.getState().reset();
    useWorkspaceStore.getState().reset();
    monitoring.getCurrentScope().setUser(null);
    clearAllWorkspaceProjectIdsFromLocalStorage();

    // unset user first before invalidating queries so that queries depending on
    // an isLoggedIn check are not retriggered
    // NOTE: we set to false (initial state), meaning that the user is not loaded
    // since null can be interpreted as no user / an anonymous user, see JSDoc for user
    set({ user: false, bookmarks: [] });
    await resetAnalytics();
    await resetGrowthbookAttributes();

    // clear query client cache as access permissions may have changed
    // let it to start fetching again its queries as needed later (on mount)
    queryClient.clear();

    // we need to re-call initialize so the user object is reset etc
    // otherwise it doesnt render the canvas as user===false which is deemed as "loading"
    get().initialize();
  },
  /**
   * logout the user and perform cleanup
   */
  logout: async (): Promise<void> => {
    await userApi.deleteSession();
    analytics.track(USER_LOGOUT);
    await get().removeUser();

    if (isNativeApp()) {
      sendLogoutMessage();
    }
  },

  /**
   * Registers action to trigger before login
   */
  registerBeforeLoginAction: (action: (user: User | null) => void): void => {
    const actions = get().beforeLoginActions;

    if (!actions.includes(action)) {
      set((state) => ({
        beforeLoginActions: [...state.beforeLoginActions, action],
      }));
    }
  },

  unregisterBeforeLoginAction: (action: (user: User | null) => void): void => {
    const actions = get().beforeLoginActions;
    const actionIndex = actions.findIndex((x) => x === action);

    if (actionIndex > -1) {
      set((state) => ({
        beforeLoginActions: [
          ...state.beforeLoginActions.slice(0, actionIndex),
          ...state.beforeLoginActions.slice(actionIndex + 1),
        ],
      }));
    }
  },

  /**
   * Registers action to trigger after signup
   */
  registerAfterSignupAction: (action): void => {
    set((state) => ({
      afterSignupActions: [...state.afterSignupActions, action],
    }));
  },

  /**
   * Triggers all before login actions
   */
  beforeLogin: (user: User | null): void => {
    if (user) {
      try {
        setUserContextMonitoring(user);
        monitoring.setUser({
          email: user.email,
          id: user.id,
          username: user.slug,
        });
      } catch (e) {
        console.warn('Error with sentry: ', e);
      }
    }
    get().beforeLoginActions.forEach((action) => action(user));
  },

  /**
   * Triggers all after signup actions
   */
  afterSignup: (user?: User): void => {
    get().afterSignupActions.forEach((action) => action(user));
  },

  /**
   * updates whether an element is bookmarked or not
   * both locally and in the API
   */
  updateBookmark: async (
    isBookmarked: boolean,
    id: string,
    featureOrigin?: string
  ): Promise<void> => {
    const bookmarkType = 'TEMPLATE';
    const bookmarks = get().bookmarks;

    if (!isBookmarked && !bookmarks.includes(id)) {
      // add to bookmarks
      await userApi.createBookmark(id, bookmarkType);

      // track analytics with conditional inclusion of featureOrigin and templateId
      const analyticsPayload: Record<string, unknown> = {};
      if (featureOrigin !== undefined) {
        analyticsPayload.featureOrigin = featureOrigin;
        analyticsPayload.templateId = id;
      }
      analytics.track(TEMPLATE_BOOKMARK, analyticsPayload);

      set({
        bookmarks: [id, ...bookmarks],
      });
    } else if (isBookmarked && bookmarks.includes(id)) {
      // remove from bookmarks
      await userApi.deleteBookmark(id, bookmarkType);
      set({
        bookmarks: bookmarks.filter((bId) => bId !== id),
      });
    }
  },

  getAttribute: (attributeName: string): string | undefined => {
    return get().attributes[attributeName];
  },

  fetchAttribute: async (attributeName: string): Promise<string | null> => {
    const res = await userApi.getUserAttribute({ attributeName });
    if (res && res.data) {
      set({ attributes: { ...get().attributes, [attributeName]: res.data } });
      return res.data;
    }
    return null;
  },

  createUpdateAttribute: async (attributeName, newValue): Promise<void> => {
    await userApi.createUpdateUserAttribute({ attributeName, value: newValue });
  },

  setAnonymousName: (name: string): void => {
    set({ anonymousUserName: name });
  },

  setIsSigningOut: (isSigningOut: boolean): void => {
    set({ isSigningOut });
  },
});
